import {useEvent} from 'Event/EventProvider'
import {
  ObieAttendeeLinkData,
  ObieAttendeeLinkError,
} from 'lib/event-api/integrations/obie/attendee-links/list'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import Button from 'lib/ui/Button'
import TextField from 'lib/ui/TextField'
import {getEventUrl} from 'lib/url'
import JourneySelect from 'organization/Event/Services/Apps/Obie/Config/AttendeeLinks/JourneySelect'
import MilestoneSelect from 'organization/Event/Services/Apps/Obie/Config/AttendeeLinks/MilestoneSelect'
import StepSelect from 'organization/Event/Services/Apps/Obie/Config/AttendeeLinks/StepSelect'
import React, {useState} from 'react'
import styled from 'styled-components'

export interface AttendeeLinkFormProps {
  obieHubId: string
  link: ObieAttendeeLinkData
  isSaving: boolean
  error: ObieAttendeeLinkError | null
  submitLabel: string
  onSubmit: (data: ObieAttendeeLinkData) => void
}

export default function AttendeeLinkForm(props: AttendeeLinkFormProps) {
  const {obieHubId, link, isSaving, error, submitLabel, onSubmit} = props
  const {event} = useEvent()
  const [label, setLabel] = useState(link.label)
  const [slug, setSlug] = useState(link.slug)
  const [hasEditedSlug, setHasEditedSlug] = useState(Boolean(link.slug))
  const [journeyId, setJourneyId] = useState(link.obie_journey_id)
  const [milestoneId, setMilestoneId] = useState(link.obie_milestone_id)
  const [stepId, setStepId] = useState(link.obie_step_id)

  const isSlugValid = isValidSlug(slug)
  const canSave =
    !isSaving && label.trim() !== '' && isSlugValid && journeyId !== ''
  const slugError = error?.errors?.slug?.[0]
  const formError = slugError ? null : error?.message ?? null
  const slugHelperText = getSlugHelperText({
    slug,
    slugError,
    eventUrl: getEventUrl(event),
  })

  const handleLabelChange = (value: string) => {
    setLabel(value)

    if (!hasEditedSlug) {
      setSlug(getSlug(value))
    }
  }

  const handleSlugChange = (value: string) => {
    setSlug(value)
    setHasEditedSlug(true)
  }

  const handleJourneyChange = (obieJourneyId: string) => {
    setJourneyId(obieJourneyId)
    setMilestoneId(null)
    setStepId(null)
  }

  const handleMilestoneChange = (obieMilestoneId: string | null) => {
    setMilestoneId(obieMilestoneId)
    setStepId(null)
  }

  const handleSubmit = (submit: React.FormEvent) => {
    submit.preventDefault()

    if (!canSave) {
      return
    }

    onSubmit({
      label: label.trim(),
      slug,
      obie_journey_id: journeyId,
      obie_milestone_id: milestoneId,
      obie_step_id: stepId,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label="Label"
        variant="outlined"
        value={label}
        onChange={handleLabelChange}
        disabled={isSaving}
        inputProps={{'aria-label': 'attendee link label'}}
        fullWidth
      />

      <TextField
        label="Slug"
        variant="outlined"
        value={slug}
        onChange={handleSlugChange}
        disabled={isSaving}
        error={Boolean(slugError) || (slug !== '' && !isSlugValid)}
        helperText={slugHelperText}
        inputProps={{'aria-label': 'attendee link slug'}}
        fullWidth
      />

      <JourneySelect
        obieHubId={obieHubId}
        value={journeyId}
        onChange={handleJourneyChange}
        disabled={isSaving}
      />

      {journeyId && (
        <MilestoneSelect
          obieJourneyId={journeyId}
          value={milestoneId}
          onChange={handleMilestoneChange}
          disabled={isSaving}
        />
      )}

      {milestoneId && (
        <StepSelect
          obieMilestoneId={milestoneId}
          value={stepId}
          onChange={setStepId}
          disabled={isSaving}
        />
      )}

      <ErrorAlert>{formError}</ErrorAlert>

      <SaveButton
        type="submit"
        variant="contained"
        color="primary"
        aria-label="save attendee link"
        disabled={!canSave}
        fullWidth
      >
        {submitLabel}
      </SaveButton>
    </form>
  )
}

function isValidSlug(slug: string) {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)
}

function getSlug(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getSlugHelperText(params: {
  slug: string
  slugError: string | undefined
  eventUrl: string
}) {
  const {slug, slugError, eventUrl} = params

  if (slugError) {
    return slugError
  }

  if (slug !== '' && !isValidSlug(slug)) {
    return 'Use lowercase letters, numbers and hyphens.'
  }

  return `${eventUrl}/obie/${slug}`
}

const SaveButton = styled(Button)`
  margin-top: ${(props) => props.theme.spacing[4]};
`
